// utils/performance.ts
// types/performance.d.ts
interface PerformanceMetrics {
    // P2 网络指标
    P2?: number;                    // responseEnd - navigationStart
    P2_1?: number;                  // domainLookupEnd - domainLookupStart
    P2_2?: number;                  // connectEnd - connectStart
    P2_3?: number;                  // connectEnd - secureConnectionStart
    P2_4?: number;                  // responseStart - requestStart
    P2_5?: number;                  // responseEnd - responseStart

    // P3 框架指标
    P3?: number;                    // routerReady - responseEnd
    P3_1?: number;                  // frameworkScriptLoaded - domContentLoadedEventStart
    P3_2?: number;                  // appMounted - frameworkScriptLoaded
    P3_3?: number;                  // routerReady - appMounted

    // 元数据
    timestamp: number;
    url: string;
    userAgent: string;
    navigationType?: string;
    apiVersion: 'PerformanceNavigationTiming';
}

interface MarkData {
    name: string;
    timestamp: number;
}

interface NavigationTimingMetrics {
    navigationStart: number;
    responseEnd: number;
    domainLookupStart: number;
    domainLookupEnd: number;
    connectStart: number;
    connectEnd: number;
    secureConnectionStart: number;
    requestStart: number;
    responseStart: number;
}

// utils/performance.ts
class PerformanceMonitor {
    private marks: Map<string, number> = new Map();
    private metrics: Partial<PerformanceMetrics> = {};
    private navEntry: PerformanceNavigationTiming | null = null;

    constructor() {
        this.initNavigationTiming();
    }

    // 初始化 Navigation Timing 数据 - 只使用 PerformanceNavigationTiming
    private initNavigationTiming(): void {
        if (typeof window === 'undefined' || !window.performance) {
            console.warn('Performance API is not available');
            return;
        }

        // 只使用 PerformanceNavigationTiming API
        const navEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
        if (navEntries.length > 0) {
            this.navEntry = navEntries[0];
            console.log('✅ Using PerformanceNavigationTiming API');
        } else {
            console.warn('PerformanceNavigationTiming data is not available');
        }
    }

    // 标记时间点
    public mark(name: string): void {
        const timestamp = Date.now();
        this.marks.set(name, timestamp);

        // 同时使用 Performance Mark API
        if (performance.mark) {
            performance.mark(`vue_${name}`);
        }

        console.log(`📌 Mark: ${name}`, timestamp);
    }

    // 测量两个标记之间的时间
    public measure(metricName: string, startMark: string, endMark: string): number | null {
        const startTime = this.marks.get(startMark);
        const endTime = this.marks.get(endMark);

        if (!startTime || !endTime) {
            console.warn(`Cannot measure ${metricName}: missing time marks (${startMark}, ${endMark})`);
            return null;
        }

        const duration = endTime - startTime;
        (this.metrics as any)[metricName] = duration;

        // 使用 Performance Measure API
        if (performance.measure) {
            performance.measure(`vue_${metricName}`, `vue_${startMark}`, `vue_${endMark}`);
        }

        console.log(`📊 Measure: ${metricName} = ${duration}ms`);
        return duration;
    }

    // 获取 Navigation Timing 指标
    private getNavigationTimingMetrics(): NavigationTimingMetrics | null {
        if (!this.navEntry) {
            return null;
        }

        const t = this.navEntry;
        const timeOrigin = performance.timeOrigin;

        const timingMetrics: NavigationTimingMetrics = {
            navigationStart: t.startTime,
            responseEnd: t.responseEnd,
            domainLookupStart: t.domainLookupStart,
            domainLookupEnd: t.domainLookupEnd,
            connectStart: t.connectStart,
            connectEnd: t.connectEnd,
            secureConnectionStart: t.secureConnectionStart,
            requestStart: t.requestStart,
            responseStart: t.responseStart
        }

        // 计算 P2
        const p2Value = t.responseEnd - t.startTime;
        console.log('  P2 (responseEnd - navigationStart):', p2Value);

        // 只为 P3 计算需要的时间点创建 mark
        const p3RequiredMarks = [
            { name: 'responseEnd', time: timeOrigin + t.responseEnd },
            { name: 'domContentLoadedEventStart', time: timeOrigin + t.domContentLoadedEventStart }
        ];

        p3RequiredMarks.forEach(({ name, time }) => {
            if (time > timeOrigin && performance.mark) {
                // 创建 performance.mark
                performance.mark(`vue_${name}`);

                // 同时记录到自定义 marks
                if (!this.marks.has(name)) {
                    this.marks.set(name, time);
                }

                console.log(`📌 Created P3 mark: vue_${name} at ${time}`);
            }
        });

        Object.keys(timingMetrics).forEach(key => {
            if ((timingMetrics as any)[key] === undefined) {
                delete (timingMetrics as any)[key];
            }
        });

        return timingMetrics
    }

    // 验证时间数据的有效性
    private isValidTimingValue(value: number): boolean {
        return value >= 0 && value < Number.MAX_SAFE_INTEGER;
    }

    // 计算时间差，确保数据有效
    private calculateTimeDifference(start: number, end: number): number | undefined {
        if (this.isValidTimingValue(start) && this.isValidTimingValue(end) && end >= start) {
            return end - start;
        }
        return undefined;
    }

    // 获取 P2 网络相关指标 - 使用 PerformanceNavigationTiming
    public getNetworkMetrics(): Partial<PerformanceMetrics> | null {
        const timing = this.getNavigationTimingMetrics();
        if (!timing) {
            console.warn('PerformanceNavigationTiming data is not available');
            return null;
        }

        const {
            navigationStart,
            responseEnd,
            domainLookupStart,
            domainLookupEnd,
            connectStart,
            connectEnd,
            secureConnectionStart,
            requestStart,
            responseStart
        } = timing;

        const metrics: Partial<PerformanceMetrics> = {
            // P2 网络连接总耗时
            P2: this.calculateTimeDifference(navigationStart, responseEnd),

            // P2.1 DNS查询时间
            P2_1: this.calculateTimeDifference(domainLookupStart, domainLookupEnd),

            // P2.2 TCP连接时间
            P2_2: this.calculateTimeDifference(connectStart, connectEnd),

            // P2.3 TLS握手时间（如果有安全连接）
            P2_3: secureConnectionStart > 0 ? this.calculateTimeDifference(secureConnectionStart, connectEnd) : undefined,

            // P2.4 TTFB(首字节时间)
            P2_4: this.calculateTimeDifference(requestStart, responseStart),

            // P2.5 HTML下载时间
            P2_5: this.calculateTimeDifference(responseStart, responseEnd)
        };

        // 过滤掉 undefined 值
        Object.keys(metrics).forEach(key => {
            if ((metrics as any)[key] === undefined) {
                delete (metrics as any)[key];
            }
        });

        console.log('🌐 Network Metrics:', metrics);
        return metrics;
    }

    // 获取 P3 框架初始化指标
    public getFrameworkMetrics(): Partial<PerformanceMetrics> {
        const metrics: Partial<PerformanceMetrics> = {};

        // P3 SPA框架启动耗时
        if (this.hasMark('responseEnd') && this.hasMark('routerReady')) {
            const p3 = this.measure('P3', 'responseEnd', 'routerReady');
            if (p3 !== null) metrics.P3 = p3;
        }

        // P3.1 框架脚本加载
        if (this.hasMark('frameworkScriptLoaded') && this.hasMark('domContentLoadedEventStart')) {
            const p3_1 = this.measure('P3_1', 'domContentLoadedEventStart', 'frameworkScriptLoaded');
            if (p3_1 !== null) metrics.P3_1 = p3_1;
        }

        // P3.2 应用实例化
        if (this.hasMark('appMounted') && this.hasMark('frameworkScriptLoaded')) {
            const p3_2 = this.measure('P3_2', 'frameworkScriptLoaded', 'appMounted');
            if (p3_2 !== null) metrics.P3_2 = p3_2;
        }

        // P3.3 路由初始化
        if (this.hasMark('routerReady') && this.hasMark('appMounted')) {
            const p3_3 = this.measure('P3_3', 'appMounted', 'routerReady');
            if (p3_3 !== null) metrics.P3_3 = p3_3;
        }

        console.log('Framework Metrics:', metrics);
        return metrics;
    }

    // 检查标记是否存在
    public hasMark(name: string): boolean {
        return this.marks.has(name);
    }

    // 获取所有标记
    public getMarks(): MarkData[] {
        const marks: MarkData[] = [];
        this.marks.forEach((timestamp, name) => {
            marks.push({ name, timestamp });
        });
        return marks.sort((a, b) => a.timestamp - b.timestamp);
    }

    // 获取 responseEnd 的绝对时间（用于计算 P3）
    public getResponseEndTime(): number | null {
        if (!this.navEntry) {
            return null;
        }

        // PerformanceNavigationTiming 的时间是相对于 timeOrigin 的
        // 需要转换为绝对时间
        const responseEndRelative = this.navEntry.responseEnd;
        if (!this.isValidTimingValue(responseEndRelative)) {
            return null;
        }

        const absoluteTime = performance.timeOrigin + responseEndRelative;

        // 标记 responseEnd 时间点（如果还没有标记的话）
        if (!this.hasMark('responseEnd')) {
            this.marks.set('responseEnd', absoluteTime);
        }

        return absoluteTime;
    }

    // 获取所有指标
    public getAllMetrics(): PerformanceMetrics {
        // 确保 responseEnd 时间点已设置
        this.getResponseEndTime();

        const networkMetrics = this.getNetworkMetrics() || {};
        const frameworkMetrics = this.getFrameworkMetrics() || {};

        const allMetrics: PerformanceMetrics = {
            timestamp: Date.now(),
            url: typeof window !== 'undefined' ? window.location.href : '',
            userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
            navigationType: this.navEntry ? this.navEntry.type : undefined,
            apiVersion: 'PerformanceNavigationTiming',
            ...networkMetrics,
            ...frameworkMetrics
        };

        // 记录到 metrics 对象中
        Object.assign(this.metrics, allMetrics);

        return allMetrics;
    }

    // 重置所有数据
    public reset(): void {
        this.marks.clear();
        this.metrics = {};

        // 重新初始化导航时序数据
        this.initNavigationTiming();

        // 清理 Performance API 的 marks 和 measures
        if (performance.clearMarks) {
            performance.clearMarks();
        }
        if (performance.clearMeasures) {
            performance.clearMeasures();
        }

        console.log('Performance monitor reset');
    }
}

// 创建单例实例
const performanceMonitor = new PerformanceMonitor();

export default performanceMonitor;